import {useEffect, useState} from "react";
import {useAuth} from "@/context/use-auth";
import {
    getEmailTemplateTypes,
    getEmailTemplate,
    saveEmailTemplate,
    sendTestEmail,
    EmailTemplateResponse,
    TemplateTypeInfo,
} from "@/services/email-template-services";
import {Button} from "@/components/ui/button";
import {useToast} from "@/hooks/use-toast";
import {Check, Pencil, Send} from "lucide-react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {useAdminTranslation} from "@/i18n/hooks";
import {Text} from "@/components/tailwind/text";
import {Field, Label} from "@/components/tailwind/fieldset";
import {Input} from "@/components/tailwind/input";
import {Textarea} from "@/components/tailwind/textarea";
import {Select} from "@/components/tailwind/select";
import {Badge} from "@/components/tailwind/badge";
import log from "@/lib/logger";

export default function SectionEmailTemplates() {
    const {token} = useAuth();
    const {toast} = useToast();
    const {t} = useAdminTranslation();

    const [templateTypes, setTemplateTypes] = useState<TemplateTypeInfo[]>([]);
    const [selectedType, setSelectedType] = useState<string>("");
    const [template, setTemplate] = useState<EmailTemplateResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [testSendStatus, setTestSendStatus] = useState<"idle" | "accepted" | "error">("idle");

    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [testEmail, setTestEmail] = useState("");

    useEffect(() => {
        loadTypes();
    }, [token]);

    useEffect(() => {
        if (selectedType) {
            loadTemplate(selectedType);
        }
    }, [selectedType, token]);

    async function loadTypes() {
        try {
            const types = await getEmailTemplateTypes(token);
            if (types && types.length > 0) {
                setTemplateTypes(types);
                setSelectedType(types[0].type);
            }
        } catch (error) {
            log.error("Error fetching email template types:", error);
        } finally {
            setIsLoading(false);
        }
    }

    async function loadTemplate(templateType: string) {
        try {
            const data = await getEmailTemplate(token, templateType);
            setTemplate(data ?? null);
        } catch (error) {
            log.error("Error fetching email template:", error);
            setTemplate(null);
        }
    }

    function getCurrentTypeInfo(): TemplateTypeInfo | undefined {
        return templateTypes.find(t => t.type === selectedType);
    }

    function startEditing() {
        if (template) {
            setSubject(template.subject);
            setBody(template.body);
        } else {
            setSubject("");
            setBody("");
        }
        setIsEditing(true);
    }

    function cancelEditing() {
        setIsEditing(false);
    }

    function insertPlaceholder(placeholder: string) {
        setBody(prev => prev + `{{${placeholder}}}`);
    }

    async function save() {
        if (!subject.trim() || !body.trim()) return;
        setIsSaving(true);
        try {
            await saveEmailTemplate(token, selectedType, {
                subject: subject.trim(),
                body: body.trim(),
            });
            toast({
                title: t("adminSettings.emailTemplates.toast.saveSuccess.title"),
                description: t("adminSettings.emailTemplates.toast.saveSuccess.description"),
            });
            setIsEditing(false);
            await loadTemplate(selectedType);
        } catch (error) {
            log.error("Error saving email template:", error);
            toast({
                title: t("adminSettings.emailTemplates.toast.saveError.title"),
                description: t("adminSettings.emailTemplates.toast.saveError.description"),
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    }

    async function handleSendTestEmail() {
        if (!testEmail.trim()) return;
        setIsSendingTest(true);
        setTestSendStatus("idle");
        try {
            await sendTestEmail(token, selectedType, {recipientEmail: testEmail.trim()});
            setTestSendStatus("accepted");
            setTimeout(() => setTestSendStatus("idle"), 5000);
        } catch (error) {
            log.error("Error sending test email:", error);
            setTestSendStatus("error");
            setTimeout(() => setTestSendStatus("idle"), 5000);
        } finally {
            setIsSendingTest(false);
        }
    }

    const typeInfo = getCurrentTypeInfo();

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold">
                        {t("adminSettings.emailTemplates.title")}
                    </CardTitle>

                    {!isEditing && template && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={startEditing}
                            className="flex items-center"
                            aria-label={t("adminSettings.emailTemplates.button.edit.ariaLabel")}
                            data-testid="edit-email-template-button"
                        >
                            <Pencil className="h-4 w-4 mr-1"/>
                            {t("adminSettings.emailTemplates.button.edit.text")}
                        </Button>
                    )}
                </div>
                <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t("adminSettings.emailTemplates.description")}
                </Text>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {t("adminSettings.emailTemplates.loading")}
                    </div>
                ) : templateTypes.length === 0 ? (
                    <Text className="text-sm text-gray-500 dark:text-gray-400">
                        {t("adminSettings.emailTemplates.noTypes")}
                    </Text>
                ) : (
                    <div className="space-y-4">
                        {templateTypes.length > 1 && (
                            <Field>
                                <Label>{t("adminSettings.emailTemplates.fields.templateType")}</Label>
                                <Select
                                    value={selectedType}
                                    onChange={(e) => {
                                        setSelectedType(e.target.value);
                                        setIsEditing(false);
                                    }}
                                    data-testid="template-type-select"
                                >
                                    {templateTypes.map(type => (
                                        <option key={type.type} value={type.type}>
                                            {t(`adminSettings.emailTemplates.templateType.${type.type}`, type.type)}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                        )}

                        {isEditing ? (
                            <div className="space-y-4">
                                <Field>
                                    <Label>{t("adminSettings.emailTemplates.fields.subject")}</Label>
                                    <Input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder={t("adminSettings.emailTemplates.placeholder.subject")}
                                        data-testid="template-subject-input"
                                    />
                                </Field>

                                <Field>
                                    <Label>{t("adminSettings.emailTemplates.fields.body")}</Label>
                                    <Textarea
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        rows={10}
                                        placeholder={t("adminSettings.emailTemplates.placeholder.body")}
                                        data-testid="template-body-input"
                                    />
                                </Field>

                                {typeInfo && (
                                    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4">
                                        <Text className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                                            {t("adminSettings.emailTemplates.placeholders.title")}
                                        </Text>
                                        <div className="flex flex-wrap gap-2">
                                            {typeInfo.placeholders.map(placeholder => (
                                                <button
                                                    key={placeholder}
                                                    type="button"
                                                    onClick={() => insertPlaceholder(placeholder)}
                                                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono
                                                        bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200
                                                        hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors cursor-pointer"
                                                    title={`${t("adminSettings.emailTemplates.placeholders.insert")}: {{${placeholder}}} → ${typeInfo.dummyValues[placeholder] ?? ""}`}
                                                >
                                                    {`{{${placeholder}}}`}
                                                </button>
                                            ))}
                                        </div>
                                        <Text className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                                            {t("adminSettings.emailTemplates.placeholders.hint")}
                                        </Text>
                                    </div>
                                )}

                                <div className="flex space-x-2">
                                    <Button
                                        size="sm"
                                        onClick={save}
                                        disabled={isSaving || !subject.trim() || !body.trim()}
                                        className="flex items-center"
                                        data-testid="save-email-template-button"
                                    >
                                        <Check className="h-4 w-4 mr-1"/>
                                        {isSaving
                                            ? t("adminSettings.emailTemplates.button.save.saving")
                                            : t("adminSettings.emailTemplates.button.save.save")}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={cancelEditing}
                                        disabled={isSaving}
                                    >
                                        {t("adminSettings.emailTemplates.button.cancel")}
                                    </Button>
                                </div>
                            </div>
                        ) : template ? (
                            <div className="space-y-4">
                                {templateTypes.length === 1 && (
                                    <div className="flex items-center gap-2" data-testid="display-template-type">
                                        <Text className="text-sm font-medium">
                                            {t("adminSettings.emailTemplates.fields.templateType")}:
                                        </Text>
                                        <Badge color="blue">
                                            {t(`adminSettings.emailTemplates.templateType.${selectedType}`, selectedType)}
                                        </Badge>
                                    </div>
                                )}
                                <div>
                                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                                        {t("adminSettings.emailTemplates.fields.subject")}
                                    </Text>
                                    <Text className="text-sm font-medium" data-testid="display-template-subject">
                                        {template.subject}
                                    </Text>
                                </div>

                                <div>
                                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                                        {t("adminSettings.emailTemplates.fields.body")}
                                    </Text>
                                    <pre className="mt-1 text-sm font-mono whitespace-pre-wrap bg-gray-50 dark:bg-zinc-900 rounded-lg p-3 border border-gray-200 dark:border-zinc-700"
                                         data-testid="display-template-body">
                                        {template.body}
                                    </pre>
                                </div>

                                <div className="border-t border-gray-200 dark:border-zinc-700 pt-4">
                                    <Text className="text-sm font-medium mb-2">
                                        {t("adminSettings.emailTemplates.testEmail.title")}
                                    </Text>
                                    <div className="flex gap-2 items-end">
                                        <Field className="flex-1">
                                            <Label>{t("adminSettings.emailTemplates.testEmail.recipient")}</Label>
                                            <Input
                                                type="email"
                                                value={testEmail}
                                                onChange={(e) => setTestEmail(e.target.value)}
                                                placeholder={t("adminSettings.emailTemplates.testEmail.placeholder")}
                                                data-testid="test-email-input"
                                            />
                                        </Field>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleSendTestEmail}
                                            disabled={isSendingTest || !testEmail.trim()}
                                            className={`flex items-center transition-colors ${
                                                testSendStatus === "accepted"
                                                    ? "border-green-500 text-green-600 dark:border-green-400 dark:text-green-400"
                                                    : testSendStatus === "error"
                                                        ? "border-red-500 text-red-600 dark:border-red-400 dark:text-red-400"
                                                        : ""
                                            }`}
                                            data-testid="send-test-email-button"
                                        >
                                            {testSendStatus === "accepted" ? (
                                                <Check className="h-4 w-4 mr-1"/>
                                            ) : (
                                                <Send className="h-4 w-4 mr-1"/>
                                            )}
                                            {isSendingTest
                                                ? t("adminSettings.emailTemplates.testEmail.sending")
                                                : testSendStatus === "accepted"
                                                    ? t("adminSettings.emailTemplates.testEmail.sent")
                                                    : t("adminSettings.emailTemplates.testEmail.send")}
                                        </Button>
                                    </div>
                                    {testSendStatus === "accepted" && (
                                        <Text className="text-xs text-green-600 dark:text-green-400 mt-2"
                                              data-testid="test-email-status-accepted">
                                            {t("adminSettings.emailTemplates.testEmail.statusAccepted")}
                                        </Text>
                                    )}
                                    {testSendStatus === "error" && (
                                        <Text className="text-xs text-red-600 dark:text-red-400 mt-2"
                                              data-testid="test-email-status-error">
                                            {t("adminSettings.emailTemplates.testEmail.statusError")}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <Text className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("adminSettings.emailTemplates.notConfigured")}
                                </Text>
                                <Button
                                    size="sm"
                                    onClick={startEditing}
                                    data-testid="configure-email-template-button"
                                >
                                    {t("adminSettings.emailTemplates.button.configure")}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
